import { __assign } from "tslib";
import { memoizeFunction } from '@fluentui/react';
import { isSharedUXDarkModeOn, isThemingEnabled } from '@ms/acui-theme';
import { getModernUxExperimentPreference } from '../utilities';
import { defaultAppColorPalette } from './appColorPalette';
import { getModernUxDisabledColor } from './getModernUxDisabledColor';
var actionControlSemanticColors = {
    defaultBackgroundColor: defaultAppColorPalette.gray20,
    hoverBackgroundColor: defaultAppColorPalette.gray30,
    pressedBackgroundColor: defaultAppColorPalette.gray50,
    toggledBackgroundColor: defaultAppColorPalette.gray40,
    disabledBackgroundColor: defaultAppColorPalette.gray20,
    focusedBackgroundColor: 'transparent',
    textColor: defaultAppColorPalette.gray180,
    textDisabledColor: defaultAppColorPalette.gray90,
    disabledBorderColor: defaultAppColorPalette.gray20,
    focusedBorderColor: defaultAppColorPalette.gray130,
    focusDisabledBackgroundColor: 'transparent',
    placeholderTextColor: defaultAppColorPalette.gray130,
    tabHeaderBackgroundColor: defaultAppColorPalette.gray20,
    tabHeaderHoverBackgroundColor: defaultAppColorPalette.gray30,
    tabHeaderPressedBackgroundColor: defaultAppColorPalette.gray50,
    tabHeaderFocusedBorderColor: defaultAppColorPalette.gray130,
    activeTabHeaderBackgroundColor: defaultAppColorPalette.gray20,
    activeTabHeaderTextColor: defaultAppColorPalette.gray180,
    activeTabHeaderHoverBackgroundColor: defaultAppColorPalette.gray30,
    activeTabHeaderHoverTextColor: defaultAppColorPalette.gray180,
    activeTabHeaderPressedBackgroundColor: defaultAppColorPalette.gray50,
    activeTabHeaderPressedTextColor: defaultAppColorPalette.gray180,
    contextualTabHeaderBackgroundColor: defaultAppColorPalette.white,
    /**
     * contextualTabHeaderTextColor without the theme is appColors.primary, but we have no access to appColors here
     * Usage of this will be guarded by the theming flight, and if not on should use appColors directly
     */
    contextualTabHeaderTextColor: '',
    contextualTabHeaderHoverBackgroundColor: defaultAppColorPalette.gray30,
    tellMeControlActiveBackgroundColor: defaultAppColorPalette.white,
    tellMeControlDefaultBackgroundColor: defaultAppColorPalette.gray20,
    tellMeControlBorderColor: defaultAppColorPalette.white,
};
var actionControlSemanticColorsWithTheme = memoizeFunction(function (theme) {
    return {
        defaultBackgroundColor: theme.palette.neutralLighterAlt,
        hoverBackgroundColor: theme.palette.neutralLighter,
        pressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
        toggledBackgroundColor: theme.palette.neutralLight,
        disabledBackgroundColor: theme.palette.neutralLighterAlt,
        focusedBackgroundColor: 'transparent',
        textColor: theme.palette.neutralDark,
        textDisabledColor: theme.palette.neutralTertiaryAlt,
        disabledBorderColor: theme.palette.neutralLighterAlt,
        focusedBorderColor: theme.palette.neutralPrimaryAlt,
        focusDisabledBackgroundColor: 'transparent',
        placeholderTextColor: theme.palette.neutralPrimaryAlt,
        tabHeaderBackgroundColor: theme.palette.neutralLighterAlt,
        tabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
        tabHeaderPressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
        tabHeaderFocusedBorderColor: theme.palette.neutralPrimaryAlt,
        activeTabHeaderBackgroundColor: theme.palette.neutralLighterAlt,
        activeTabHeaderTextColor: theme.palette.neutralDark,
        activeTabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
        activeTabHeaderHoverTextColor: theme.palette.neutralDark,
        activeTabHeaderPressedBackgroundColor: theme.palette.neutralQuaternaryAlt,
        activeTabHeaderPressedTextColor: theme.palette.neutralDark,
        contextualTabHeaderBackgroundColor: theme.palette.white,
        contextualTabHeaderTextColor: theme.palette.themePrimary,
        contextualTabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
        tellMeControlActiveBackgroundColor: theme.palette.white,
        tellMeControlDefaultBackgroundColor: theme.palette.neutralLighterAlt,
        tellMeControlBorderColor: theme.palette.white,
        itemDividerColor: theme.palette.neutralQuaternary,
    };
});
var darkModeActionControlSemanticColors = memoizeFunction(function (theme) {
    return __assign(__assign({}, actionControlSemanticColorsWithTheme(theme)), { tellMeControlActiveBackgroundColor: theme.palette.neutralQuaternaryAlt, tellMeControlDefaultBackgroundColor: theme.palette.neutralQuaternaryAlt });
});
var modernUxActionControlSemanticColors = memoizeFunction(function (theme) {
    return {
        defaultBackgroundColor: theme.palette.white,
        hoverBackgroundColor: theme.palette.neutralLighter,
        pressedBackgroundColor: theme.palette.neutralTertiaryAlt,
        toggledBackgroundColor: theme.palette.neutralQuaternary,
        disabledBackgroundColor: theme.palette.white,
        focusedBackgroundColor: 'transparent',
        textColor: theme.palette.neutralDark,
        textDisabledColor: getModernUxDisabledColor(theme.palette.neutralDark),
        disabledBorderColor: theme.palette.white,
        focusedBorderColor: theme.palette.neutralPrimaryAlt,
        focusDisabledBackgroundColor: 'transparent',
        placeholderTextColor: theme.palette.neutralPrimaryAlt,
        tabHeaderBackgroundColor: 'transparent',
        tabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
        tabHeaderPressedBackgroundColor: theme.palette.neutralTertiaryAlt,
        tabHeaderFocusedBorderColor: theme.palette.neutralPrimaryAlt,
        activeTabHeaderBackgroundColor: 'transparent',
        activeTabHeaderTextColor: theme.palette.themePrimary,
        activeTabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
        activeTabHeaderHoverTextColor: theme.palette.themePrimary,
        activeTabHeaderPressedBackgroundColor: theme.palette.neutralTertiaryAlt,
        activeTabHeaderPressedTextColor: theme.palette.themePrimary,
        contextualTabHeaderBackgroundColor: 'transparent',
        contextualTabHeaderTextColor: theme.palette.themePrimary,
        contextualTabHeaderHoverBackgroundColor: theme.palette.neutralLighter,
        tellMeControlActiveBackgroundColor: theme.palette.neutralLight,
        tellMeControlDefaultBackgroundColor: theme.palette.white,
        tellMeControlBorderColor: theme.palette.neutralPrimaryAlt,
        itemDividerColor: theme.palette.neutralQuaternary,
    };
});
export var getActionControlSemanticColorsWithTheme = function (theme) {
    if (isThemingEnabled()) {
        if (isSharedUXDarkModeOn()) {
            return darkModeActionControlSemanticColors(theme);
        }
        if (getModernUxExperimentPreference()) {
            return modernUxActionControlSemanticColors(theme);
        }
        return actionControlSemanticColorsWithTheme(theme);
    }
    return actionControlSemanticColors;
};
//# sourceMappingURL=actionControlSemanticColors.js.map